import { useRef, useState, type CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import { NewProjectSheet, ProjectGrid } from '../components/ProjectCard.js';
import { createLifeNote, KEEPALIVE } from '../lifeos/api.js';
import { clock, firstLine, humanize, prettyHandle, shortDate, stamp, ymdInHomeZone } from '../lifeos/format.js';
import FormSheet from '../lifeos/FormSheet.js';
import { text } from '../lifeos/forms.js';
import { useSpheres } from '../lifeos/spheres.js';
import { useLoad } from '../lifeos/useLoad.js';
import type { LifeNote } from '../lifeos/types.js';
import {
  deleteWorkspaceFile,
  getWorkspace,
  refreshWorkspaces,
  updateWorkspace,
  updateWorkspaceFile,
  uploadWorkspaceFile,
  type SphereFile,
  type WorkspaceDetail,
} from '../lib/workspaces.js';
import { groupMeetings } from '../lib/meetings.js';
import { Button, EmptyState, Group, Icon, IconButton, Loader, Page, Row, useToast } from '../ui/index.js';

/** '1.2 MB' (same wording as project files). */
function fileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** 'Today 2:30 PM' / 'Thu Oct 16, 2:30 PM'. */
export function meetingWhen(iso: string, today = ymdInHomeZone(new Date())): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const ymd = ymdInHomeZone(d);
  if (ymd === today) return `Today ${clock(iso)}`;
  return stamp(iso);
}

/** Knowledge-base notes shown before "Show all N". */
const NOTES_SHOWN = 6;

/** Most recently touched first. */
function recentFirst(notes: readonly LifeNote[]): LifeNote[] {
  const at = (n: LifeNote): string => n.updatedAt ?? n.createdAt ?? '';
  return [...notes].sort((a, b) => at(b).localeCompare(at(a)));
}

type SheetState = null | { kind: 'project' } | { kind: 'note' } | { kind: 'file'; file: File } | { kind: 'editFile'; file: SphereFile };

/**
 * A workspace (`/w/:key`, spec 2026-10-14): one job with everything under it —
 * a one-line description, four numbers, the projects as cards, the knowledge
 * base (notes + files), its people, what is waiting for a reply and the next
 * two weeks of meetings.
 */
export default function Workspace(): JSX.Element {
  const { key = '' } = useParams();
  const spheres = useSpheres();
  const toast = useToast();
  const ws = useLoad(() => getWorkspace(key), [key]);
  const [sheet, setSheet] = useState<SheetState>(null);
  const [gone, setGone] = useState<ReadonlySet<string>>(new Set());
  const [allNotes, setAllNotes] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  function removeFile(f: SphereFile): void {
    setGone((s) => new Set(s).add(f.id));
    toast.defer({
      text: `Removed ${f.name}`,
      commit: () => deleteWorkspaceFile(key, f.id, KEEPALIVE),
      onUndo: () =>
        setGone((s) => {
          const n = new Set(s);
          n.delete(f.id);
          return n;
        }),
      onDone: () => void ws.reload(),
    });
  }

  const name = ws.data?.workspace.name ?? spheres.find((s) => s.key === key)?.name ?? humanize(key);
  const color = ws.data?.workspace.color ?? spheres.find((s) => s.key === key)?.color ?? null;

  return (
    <Page
      title={name}
      wide
      className="ws"
      back={{ to: '/projects', label: 'Projects' }}
      actions={
        <Button icon="plus" size="sm" onClick={() => setSheet({ kind: 'project' })}>
          Project
        </Button>
      }
    >
      <Loader state={ws}>
        {(d) => (
          <div className="ws-body" style={{ '--job': color ?? 'var(--accent)' } as CSSProperties}>
            <Description wsKey={key} value={d.workspace.description} onSaved={() => void ws.reload()} />
            <Stats d={d} wsKey={key} />

            <section className="ws-sec" aria-labelledby="ws-projects">
              <div className="ws-sec__head">
                <h2 className="ws-sec__title" id="ws-projects">
                  Projects
                </h2>
                <button type="button" className="ui-group__action" onClick={() => setSheet({ kind: 'project' })}>
                  + Project
                </button>
              </div>
              {d.projects.filter((p) => p.status !== 'archived').length > 0 ? (
                <ProjectGrid projects={d.projects.filter((p) => p.status !== 'archived')} color={color} />
              ) : (
                <EmptyState
                  icon="folder"
                  title="No projects here yet."
                  action={
                    <Button variant="secondary" icon="plus" onClick={() => setSheet({ kind: 'project' })}>
                      Add a project
                    </Button>
                  }
                />
              )}
            </section>

            <div className="ws-cols">
              <div className="ws-col">
                <section className="ws-sec" aria-labelledby="ws-kb">
                  <div className="ws-sec__head">
                    <h2 className="ws-sec__title" id="ws-kb">
                      Knowledge base
                    </h2>
                    <button type="button" className="ui-group__action" onClick={() => setSheet({ kind: 'note' })}>
                      + Note
                    </button>
                  </div>
                  {d.notes.length > 0 ? (
                    <>
                      <div className="ws-notes">
                        {(allNotes ? recentFirst(d.notes) : recentFirst(d.notes).slice(0, NOTES_SHOWN)).map((n) => (
                          <NoteCard key={n.id} note={n} />
                        ))}
                      </div>
                      {d.notes.length > NOTES_SHOWN && (
                        <button type="button" className="ws-more" aria-expanded={allNotes} onClick={() => setAllNotes((v) => !v)}>
                          {allNotes ? 'Show fewer' : `Show all ${d.notes.length}`}
                        </button>
                      )}
                    </>
                  ) : (
                    <p className="ws-none">No notes yet. Add what you always have to look up: logins live elsewhere, but process, decisions and context belong here.</p>
                  )}
                </section>

                <input
                  ref={picker}
                  type="file"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) setSheet({ kind: 'file', file: f });
                  }}
                />
                <Group header="Files" action={{ label: 'Upload', onClick: () => picker.current?.click() }}>
                  {d.files
                    .filter((f) => !gone.has(f.id))
                    .map((f) => (
                      <Row
                        key={f.id}
                        leading={<Icon name="file" size={20} />}
                        title={f.name}
                        sub={[f.about, fileSize(f.sizeBytes)].filter(Boolean).join(' · ')}
                        onClick={() => window.open(`/api/files/${encodeURIComponent(f.fileId)}`, '_blank', 'noopener')}
                        chevron={false}
                        trailing={<IconButton icon="pencil" label={`Rename or describe ${f.name}`} onClick={() => setSheet({ kind: 'editFile', file: f })} />}
                        swipe={{ trailing: [{ label: 'Remove', icon: 'trash', tone: 'bad', onAction: () => removeFile(f) }] }}
                      />
                    ))}
                  {d.files.filter((f) => !gone.has(f.id)).length === 0 && (
                    <Row tone="muted" title="No files yet — upload one" onClick={() => picker.current?.click()} chevron={false} />
                  )}
                </Group>
              </div>

              <div className="ws-col">
                <Group header="To reply" footer={d.toReply.length === 0 ? 'Nothing waiting on you.' : undefined}>
                  {d.toReply.map((m) => (
                    <Row
                      key={m.id}
                      to="/inbox?status=needs_reply"
                      leading={<Icon name="mail" size={20} />}
                      title={prettyHandle(m.fromName ?? m.handle)}
                      sub={m.subject ?? firstLine(m.text, 80)}
                      detail={shortDate(ymdInHomeZone(new Date(m.sentAt)))}
                    />
                  ))}
                  {d.toReply.length === 0 && <Row tone="muted" title="All caught up" />}
                </Group>

                <Group header="Meetings" footer="The next 14 days.">
                  {groupMeetings(d.meetings).map(({ key: k, next: m, series, when }) => (
                    <Row
                      key={k}
                      to={m.projectId ? `/projects/${encodeURIComponent(m.projectId)}` : '/plan'}
                      leading={<Icon name={series ? 'repeat' : 'calendar'} size={20} />}
                      title={m.title}
                      sub={series ? when : meetingWhen(m.startsAt)}
                    />
                  ))}
                  {d.meetings.length === 0 && <Row tone="muted" title="No meetings booked" />}
                </Group>

                <Group header="People">
                  {d.contacts.map((c) => (
                    <Row
                      key={c.id}
                      to={`/people/${c.id}`}
                      title={c.name}
                      sub={
                        c.tags?.length ? (
                          <span className="ws-tags">
                            {c.tags.map((t) => (
                              <span key={t} className="ws-tag">
                                {t}
                              </span>
                            ))}
                          </span>
                        ) : undefined
                      }
                    />
                  ))}
                  {d.contacts.length === 0 && <Row tone="muted" title="No people linked to this workspace yet" />}
                </Group>
              </div>
            </div>

            {sheet?.kind === 'project' && (
              <NewProjectSheet
                spheres={spheres}
                sphereKey={key}
                parents={d.projects.map((p) => ({ id: p.id, name: p.name }))}
                onClose={() => setSheet(null)}
                onCreated={() => {
                  void ws.reload();
                  refreshWorkspaces();
                }}
              />
            )}
            {sheet?.kind === 'note' && (
              <FormSheet
                title="Add a note"
                submitLabel="Add"
                fields={[
                  { key: 'title', label: 'Title', wide: true },
                  { key: 'body', label: 'Note', kind: 'textarea', required: true, wide: true },
                ]}
                initial={{ title: '', body: '' }}
                onClose={() => setSheet(null)}
                onSubmit={async (v) => {
                  const title = text(v.title);
                  // No projectId: a workspace note is the job's own knowledge base.
                  await createLifeNote({ ...(title ? { title } : {}), body: text(v.body)!, sphereKey: key });
                  void ws.reload();
                }}
              />
            )}
            {sheet?.kind === 'file' && (
              <FormSheet
                title="Upload a file"
                submitLabel="Upload"
                fields={[
                  { key: 'name', label: 'Name', required: true, wide: true, hint: sheet.file.name },
                  { key: 'about', label: 'What is it?', wide: true },
                ]}
                initial={{ name: sheet.file.name.replace(/\.[^.]{1,5}$/, ''), about: '' }}
                onClose={() => setSheet(null)}
                onSubmit={async (v) => {
                  await uploadWorkspaceFile(key, sheet.file, { name: text(v.name) ?? undefined, about: text(v.about) ?? undefined });
                  void ws.reload();
                }}
              />
            )}
            {sheet?.kind === 'editFile' && (
              <FormSheet
                title={`Edit ${sheet.file.name}`}
                fields={[
                  { key: 'name', label: 'Name', required: true, wide: true, hint: sheet.file.originalName ?? undefined },
                  { key: 'about', label: 'What is it?', wide: true },
                ]}
                initial={{ name: sheet.file.name, about: sheet.file.about ?? '' }}
                danger={{
                  label: 'Remove file',
                  onClick: () => removeFile(sheet.file),
                }}
                onClose={() => setSheet(null)}
                onSubmit={async (v) => {
                  await updateWorkspaceFile(key, sheet.file.id, { name: text(v.name)!, about: text(v.about) ?? null });
                  void ws.reload();
                }}
              />
            )}
          </div>
        )}
      </Loader>
    </Page>
  );
}

/** The workspace's one-line description, edited in place (Enter saves, Esc cancels). */
function Description({ wsKey, value, onSaved }: { wsKey: string; value: string | null; onSaved: () => void }): JSX.Element {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [busy, setBusy] = useState(false);

  async function save(): Promise<void> {
    const next = draft.trim() || null;
    if (next === (value ?? null)) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await updateWorkspace(wsKey, { description: next });
      setEditing(false);
      onSaved();
      refreshWorkspaces();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the description');
    } finally {
      setBusy(false);
    }
  }

  if (!editing)
    return (
      <button
        type="button"
        className={`ws-desc${value ? '' : ' ws-desc--empty'}`}
        onClick={() => {
          setDraft(value ?? '');
          setEditing(true);
        }}
        aria-label={value ? `Description: ${value}. Edit` : 'Add a description'}
      >
        <span>{value || 'Add a one-line description of this job…'}</span>
        <Icon name="pencil" size={14} className="ws-desc__pen" />
      </button>
    );
  return (
    <form
      className="ws-desc-form"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <input
        className="ws-desc-input"
        aria-label="Workspace description"
        value={draft}
        autoFocus
        maxLength={280}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setEditing(false);
          }
        }}
      />
      <Button type="submit" size="sm" disabled={busy}>
        Save
      </Button>
      <Button variant="plain" size="sm" onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </form>
  );
}

/** Four numbers across the top: open items, this week, waiting, next meeting. */
function Stats({ d, wsKey }: { d: WorkspaceDetail; wsKey: string }): JSX.Element {
  const q = `?sphere=${encodeURIComponent(wsKey)}`;
  const next = d.meetings[0] ?? null;
  return (
    <div className="ws-stats">
      <Link to={`/tasks${q}`} className="ws-stat">
        <span className="ws-stat__label">Open items</span>
        <span className="ws-stat__value num">{d.counts.openItems}</span>
      </Link>
      <Link to={`/tasks${q}`} className="ws-stat">
        <span className="ws-stat__label">This week</span>
        <span className="ws-stat__value num">{d.counts.thisWeek}</span>
      </Link>
      <Link to={`/tasks${q}`} className="ws-stat">
        <span className="ws-stat__label">Waiting on others</span>
        <span className="ws-stat__value num">{d.counts.waiting}</span>
      </Link>
      <div className="ws-stat ws-stat--meeting">
        <span className="ws-stat__label">Next meeting</span>
        {next ? (
          <>
            <span className="ws-stat__text">{next.title}</span>
            <span className="ws-stat__sub">{meetingWhen(next.startsAt)}</span>
          </>
        ) : (
          <span className="ws-stat__sub">None in the next 2 weeks</span>
        )}
      </div>
    </div>
  );
}

/** A knowledge-base note: title + two-line preview; vault notes carry an Obsidian tag. */
function NoteCard({ note: n }: { note: LifeNote }): JSX.Element {
  const title = n.title || firstLine(n.body, 60) || 'Untitled';
  return (
    <Link to={`/notes?q=${encodeURIComponent(title)}`} className="ws-note">
      <span className="ws-note__title">
        {title}
        {n.source === 'obsidian' && <span className="ws-note__src">Obsidian</span>}
      </span>
      <span className="ws-note__body">{n.body}</span>
    </Link>
  );
}
